import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import type {
  TenantAiInstructionConfigurationResponse,
  UpdateTenantAiConfigurationRequest,
} from '@/api/schema/data-contracts';
import { SelectFormField } from '@/components/atoms/SelectFormField';
import { Unauthorized } from '@/components/atoms/Unauthorized';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { TabHeader } from '@/components/molecules/TabHeader';
import { useAiSettings } from '@/context/aiSettings';
import type { eLanguage } from '@/context/localization';
import { useLocalization } from '@/context/localization';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useHasGlobalPermission } from '@/hooks/useHasGlobalPermission';
import { AiAdvisoryAdminLayout } from '@/pages/administration/tabs/ai-advisory-layout/AiAdvisoryAdminLayout';
import { AdministrationTabsWrapper } from '@/pages/administration/tabs/wrapper';
import { parseProblemDetails } from '@/utils/errors';
import { handleValidationErrors } from '@/utils/validationErrors';

import { AiWizardMessages } from './messages';

interface AiWizardFormData {
  aiInstructions: string;
  isAiEnabled: boolean;
  selectedLanguage: string;
  advisoryDefaultModelRefKey: string;
}

// EWM-1087: the API reads null as "leave the stored choice alone", so clearing the select has to
// send something. An empty Guid is that signal.
const CLEAR_ADVISORY_DEFAULT_MODEL = '00000000-0000-0000-0000-000000000000';

type SetOfAiInstructions = Record<string, string>;

const INSTRUCTIONS_FIELD_BY_LOCALE: Record<string, 'instructionsEn' | 'instructionsDe'> = {
  'en-GB': 'instructionsEn',
  'de-DE': 'instructionsDe',
};

const getInstructionsFieldName = (
  languageCode: string,
): 'instructionsEn' | 'instructionsDe' | undefined => INSTRUCTIONS_FIELD_BY_LOCALE[languageCode];

const AiWizardTab = () => {
  const messages = useMessages(AiWizardMessages);
  const globalMessages = useMessages(GlobalMessages);
  const toast = useToast();
  const hasPermission = useHasGlobalPermission('CAN_MANAGE_TENANT_SETTINGS');
  const {
    state: { languageList },
  } = useLocalization();

  const { setAiEnabled, setAiInstructions } = useAiSettings();

  const { useGetTenantAiConfig, useUpdateTenantAiConfig, useTestAiConnection } =
    useTenantQueryActions();
  // Gate the fetch, not just the render: without this the request goes out and comes
  // back 403 before the !hasPermission early return below ever runs (EWM-950).
  const { data: aiConfig, isLoading: isLoadingConfig } = useGetTenantAiConfig({
    enabled: hasPermission === true,
  }) as {
    data: TenantAiInstructionConfigurationResponse | undefined;
    isLoading: boolean;
  };
  // EWM-1087: the convert page preselects whichever project type carries this model, so the admin
  // picks from the tenant's own published models rather than depending on the seeded one existing.
  const { useGetEstimationModelsGrouped } = useEstimationModelQueryActions();
  const { data: estimationModels } = useGetEstimationModelsGrouped(
    { isPublished: true },
    hasPermission === true,
  );
  const modelOptions = useMemo(
    () =>
      (estimationModels ?? [])
        .map((model) => ({ value: model.refKey, label: model.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [estimationModels],
  );

  const { mutateAsync: updateAiConfig, isPending } = useUpdateTenantAiConfig();
  const { mutateAsync: testConnection, isPending: isTestingConnection } = useTestAiConnection();

  const { control, handleSubmit, watch, setValue, setError, reset } = useForm<AiWizardFormData>({
    defaultValues: {
      aiInstructions: '',
      isAiEnabled: false,
      selectedLanguage: '',
      advisoryDefaultModelRefKey: '',
    },
  });

  const formIsAiEnabled = watch('isAiEnabled');
  const [selectedLanguage, setSelectedLanguage] = useState<{
    value: eLanguage;
    label: string;
  } | null>(null);

  // LOCAL STORAGE: Track AI instructions per language - preserves all typed values even before saving
  const [aiInstructionsTranslations, setAiInstructionsTranslations] = useState<SetOfAiInstructions>(
    {},
  );
  const previousLanguageRef = useRef<string | null>(null);
  const aiInstructionsTranslationsRef = useRef(aiInstructionsTranslations);
  useEffect(() => {
    aiInstructionsTranslationsRef.current = aiInstructionsTranslations;
  });

  useEffect(() => {
    if (selectedLanguage === null && languageList && languageList.length > 0) {
      const firstLanguage = { value: languageList[0].code, label: languageList[0].name };
      setSelectedLanguage(firstLanguage);
      // Keep the react-hook-form field in sync. SelectFormField renders its value from the form
      // state (not the `value` prop), so without this the language dropdown shows the placeholder
      // on entry even though the corresponding text is already loaded. EWM-740.
      setValue('selectedLanguage', firstLanguage.value);
    }
  }, [languageList, selectedLanguage, setValue]);

  // Load AI config from API into context and form
  useEffect(() => {
    if (aiConfig && !isLoadingConfig) {
      setAiEnabled(aiConfig.aiEnabled ?? false);

      // Load instructions from API and merge with existing local values (preserves unsaved changes)
      const instructionsMap: Record<string, string> = { ...aiInstructionsTranslationsRef.current };

      if (
        (aiConfig as Record<string, unknown>).instructionsEn !== null &&
        (aiConfig as Record<string, unknown>).instructionsEn !== undefined
      ) {
        instructionsMap['en-GB'] = (aiConfig as Record<string, unknown>).instructionsEn || '';
      }

      if (
        (aiConfig as Record<string, unknown>).instructionsDe !== null &&
        (aiConfig as Record<string, unknown>).instructionsDe !== undefined
      ) {
        instructionsMap['de-DE'] = (aiConfig as Record<string, unknown>).instructionsDe || '';
      }
      setAiInstructionsTranslations(instructionsMap);

      // Get instructions for the currently selected language (or first language if none selected)
      const currentLang =
        selectedLanguage?.value ||
        (languageList && languageList.length > 0 ? languageList[0].code : 'en-GB');
      const currentInstructions = instructionsMap[currentLang] || '';

      // Update ref to match current language (so language change effect doesn't fire on initial load)
      if (selectedLanguage) {
        previousLanguageRef.current = selectedLanguage.value;
      } else if (languageList && languageList.length > 0) {
        previousLanguageRef.current = languageList[0].code;
      }

      // Load all settings into form (show masked values for sensitive fields)
      reset({
        aiInstructions: currentInstructions,
        isAiEnabled: aiConfig.aiEnabled ?? false,
        // Reflect the active language in the form state so the dropdown shows it on entry. EWM-740.
        selectedLanguage: currentLang,
        advisoryDefaultModelRefKey: aiConfig.advisoryDefaultModelRefKey ?? '',
      });
    }
  }, [aiConfig, isLoadingConfig, reset, setAiEnabled, selectedLanguage, languageList]);

  // When language changes: load the new language's value from local storage.
  // Read `.value` out first so the effect depends on the language code, not the option object's
  // identity: a new object with the same code must not re-run this.
  const selectedLanguageValue = selectedLanguage?.value;
  useEffect(() => {
    if (!selectedLanguageValue) return;

    // Only update if language actually changed
    if (previousLanguageRef.current !== selectedLanguageValue) {
      const languageValue = aiInstructionsTranslationsRef.current[selectedLanguageValue] || '';
      setValue('aiInstructions', languageValue, { shouldDirty: false });
      previousLanguageRef.current = selectedLanguageValue;
    }
  }, [selectedLanguageValue, setValue]);

  // When user types: update local storage immediately
  const currentInstructions = watch('aiInstructions');
  useEffect(() => {
    if (!selectedLanguage || currentInstructions === undefined) return;

    setAiInstructionsTranslations((prev) => ({
      ...prev,
      [selectedLanguage.value]: currentInstructions,
    }));
  }, [currentInstructions, selectedLanguage]);

  const onSubmit = async (data: AiWizardFormData) => {
    const payload: UpdateTenantAiConfigurationRequest = {
      aiEnabled: data.isAiEnabled,
      // Only the loaded form may clear the choice: before the GET resolves the field still holds
      // its '' default, and an unconditional sentinel would wipe a configured model on an early save.
      advisoryDefaultModelRefKey:
        data.advisoryDefaultModelRefKey || (aiConfig ? CLEAR_ADVISORY_DEFAULT_MODEL : undefined),
    };

    // Add instructions for each language as separate fields; skip unknown locales
    Object.entries(aiInstructionsTranslations).forEach(([languageCode, instructions]) => {
      const fieldName = getInstructionsFieldName(languageCode);
      if (!fieldName) return;
      payload[fieldName] = instructions || null;
    });
    try {
      const response = await updateAiConfig(payload);

      setAiEnabled(response.aiEnabled);

      // Update aiInstructionsTranslations from response (backend returns separate fields)
      // If response doesn't have instructions, keep what we sent (they were saved successfully)
      const responseInstructionsMap: Record<string, string> = { ...aiInstructionsTranslations };

      if (
        (response as Record<string, unknown>).instructionsEn !== null &&
        (response as Record<string, unknown>).instructionsEn !== undefined
      ) {
        responseInstructionsMap['en-GB'] =
          (response as Record<string, unknown>).instructionsEn || '';
      }

      if (
        (response as Record<string, unknown>).instructionsDe !== null &&
        (response as Record<string, unknown>).instructionsDe !== undefined
      ) {
        responseInstructionsMap['de-DE'] =
          (response as Record<string, unknown>).instructionsDe || '';
      }
      // Always update with the merged map (response values or what we sent)
      setAiInstructionsTranslations(responseInstructionsMap);

      // Get the saved instructions for the current selected language
      const savedInstructions = selectedLanguage
        ? responseInstructionsMap[selectedLanguage.value] || ''
        : '';

      // Update AI settings context with current language's instructions
      if (selectedLanguage) {
        setAiInstructions(savedInstructions);
      }

      reset({
        aiInstructions: savedInstructions,
        isAiEnabled: response.aiEnabled,
        selectedLanguage: selectedLanguage?.value ?? watch('selectedLanguage') ?? '',
        advisoryDefaultModelRefKey: response.advisoryDefaultModelRefKey ?? '',
      });

      // Update form value after save to ensure it displays correctly
      if (selectedLanguage && savedInstructions !== undefined) {
        setValue('aiInstructions', savedInstructions, { shouldDirty: false });
      }

      toast.showToast(messages.apiSaveDataSuccess(), 'success');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to save AI Advisory settings:', error);

      // Handle validation errors
      const validationResult = handleValidationErrors(error, setError);

      // Parse problem details
      let problemDetails;
      try {
        problemDetails = parseProblemDetails(error);
      } catch {
        problemDetails = null;
      }

      // Build detailed error message
      const errorParts: string[] = [];

      if (validationResult.isValidationError && validationResult.messages.length > 0) {
        errorParts.push('Validation errors:');
        validationResult.messages.forEach((msg) => {
          errorParts.push(`• ${msg}`);
        });
      } else if (problemDetails) {
        // Show problem details - prioritize detail (which contains AI API error messages)
        if (problemDetails.detail) {
          errorParts.push(problemDetails.detail as string);
        } else if (problemDetails.title) {
          errorParts.push(problemDetails.title as string);
        }

        // Show error code if it's not the generic one
        if (problemDetails.errorCode && problemDetails.errorCode !== 'GENERAL') {
          errorParts.push(`Error Code: ${problemDetails.errorCode}`);
        }
      } else {
        // Fallback to generic error
        errorParts.push(messages.apiSaveDataError());
        if (error?.response?.status) {
          errorParts.push(`Status: ${error.response.status} ${error.response.statusText || ''}`);
        }
        if (error?.message) {
          errorParts.push(`Message: ${error.message}`);
        }
      }

      // Add trace ID if available
      if (problemDetails?.traceId) {
        errorParts.push(`Trace ID: ${problemDetails.traceId}`);
      }

      // Show error toast with detailed information
      const errorMessage = (
        <div className='flex flex-col gap-1'>
          {errorParts.map((part, index) => (
            // eslint-disable-next-line react/no-array-index-key -- errorParts are dynamic strings that may duplicate; index ensures uniqueness
            <div key={`error-${index}`} className={index === 0 ? 'font-semibold' : 'text-sm'}>
              {part}
            </div>
          ))}
        </div>
      );

      toast.showToast(errorMessage, 'error');
    }
  };

  const handleTestConnection = async () => {
    try {
      const result = await testConnection({});

      if (result.success) {
        toast.showToast(messages.testConnectionSuccess(), 'success');
      } else {
        const errorMessage = result.message || messages.testConnectionError();
        const errorDetails = result.errorDetails ? `\n${result.errorDetails}` : '';
        toast.showToast(`${errorMessage}${errorDetails}`, 'error');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to test AI connection:', error);

      // Parse problem details for detailed error
      let problemDetails;
      try {
        problemDetails = parseProblemDetails(error);
      } catch {
        problemDetails = null;
      }

      const errorParts: string[] = [];

      if (problemDetails) {
        if (problemDetails.detail) {
          errorParts.push(problemDetails.detail as string);
        } else if (problemDetails.title) {
          errorParts.push(problemDetails.title as string);
        }

        if (problemDetails.errorCode && problemDetails.errorCode !== 'GENERAL') {
          errorParts.push(`Error Code: ${problemDetails.errorCode}`);
        }
      } else {
        errorParts.push(messages.testConnectionError());
        if (error?.response?.status) {
          errorParts.push(`Status: ${error.response.status}`);
        }
      }

      if (problemDetails?.traceId) {
        errorParts.push(`Trace ID: ${problemDetails.traceId}`);
      }

      const errorMessage = (
        <div className='flex flex-col gap-1'>
          {errorParts.map((part, index) => (
            // eslint-disable-next-line react/no-array-index-key -- errorParts are dynamic strings that may duplicate; index ensures uniqueness
            <div key={`error-${index}`} className={index === 0 ? 'font-semibold' : 'text-sm'}>
              {part}
            </div>
          ))}
        </div>
      );

      toast.showToast(errorMessage, 'error');
    }
  };

  if (!hasPermission) {
    return <Unauthorized />;
  }

  return (
    <AdministrationTabsWrapper>
      <AiAdvisoryAdminLayout activeTab='settings'>
        <TabHeader
          title={messages.header()}
          onSave={handleSubmit(onSubmit)}
          saveButtonProcessing={isPending}
          saveButtonDisabled={isLoadingConfig}
        />
        <div className='space-y-6'>
          {/* Language Selection */}
          <div className='admin-block-item'>
            <label>{globalMessages.selectLanguage()}</label>
            <SelectFormField
              control={control}
              fieldPath='selectedLanguage'
              options={languageList.map((l) => ({ value: l.code, label: l.name }))}
              placeholder={globalMessages.selectLanguage()}
              onChange={(value) => {
                setSelectedLanguage(value as { value: eLanguage; label: string } | null);
                setValue('selectedLanguage', value?.value || '');
              }}
              value={selectedLanguage}
            />
          </div>

          {/* AI Features Toggle */}
          <div className='admin-block-item'>
            <h6 className='mb-4 text-sm font-medium text-gray-900 dark:text-gray-100'>
              {messages.aiFeaturesToggleLabel()}
            </h6>
            <div className='flex items-center space-x-3'>
              <label className='relative inline-flex cursor-pointer items-center'>
                <input
                  type='checkbox'
                  className='peer sr-only'
                  checked={formIsAiEnabled}
                  onChange={(e) => setValue('isAiEnabled', e.target.checked)}
                />
                <div className="peer peer-checked:bg-c1 dark:peer-focus:ring-c1 h-6 w-11 rounded-full bg-gray-200 peer-focus:outline-hidden after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:border-gray-600 dark:bg-gray-700" />
                <span className='ml-3 text-sm font-medium text-gray-900 dark:text-gray-300'>
                  {formIsAiEnabled ? messages.aiEnabled() : messages.aiDisabled()}
                </span>
              </label>
            </div>
          </div>

          {/* Default estimation model for advisory conversion (EWM-1087) */}
          <div className='admin-block-item'>
            <h6 className='mb-4 text-sm font-medium text-gray-900 dark:text-gray-100'>
              {messages.advisoryDefaultModelLabel()}
            </h6>
            <SelectFormField
              control={control}
              fieldPath='advisoryDefaultModelRefKey'
              options={modelOptions}
              placeholder={messages.advisoryDefaultModelPlaceholder()}
              isClearable
            />
            <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
              {messages.advisoryDefaultModelDescription()}
            </p>
          </div>

          {/* AI Instructions */}
          <div className='admin-block-item'>
            <h6 className='mb-4 text-sm font-medium text-gray-900 dark:text-gray-100'>
              {messages.aiInstructionsLabel()}
            </h6>
            <WysiwygFormTextField
              key={selectedLanguage?.value || 'en-US'}
              control={control}
              fieldPath='aiInstructions'
              placeholder={
                (selectedLanguage?.value as string) === 'de-DE'
                  ? 'Geben Sie spezifische Anweisungen für den AI-Assistenten ein...'
                  : 'Enter specific instructions for the AI assistant...'
              }
            />
          </div>

          {/* Test Connection Button */}
          <div className='admin-block-item'>
            <button
              type='button'
              onClick={handleTestConnection}
              disabled={isTestingConnection}
              className='hover:bg-c1/90 bg-c1 focus:ring-c1 inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800'
            >
              {isTestingConnection
                ? messages.testConnectionTesting()
                : messages.testConnectionButton()}
            </button>
          </div>
        </div>
      </AiAdvisoryAdminLayout>
    </AdministrationTabsWrapper>
  );
};

export default AiWizardTab;
